import {
  Component, ElementRef, Input, OnInit
} from '@angular/core';
import {
  UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators
} from '@angular/forms';
import {
  filter, takeUntil, take, concatMap
} from 'rxjs/operators';

import { COUNTRY_LIST, VALIDATORS } from 'app/constants';
import { Customer } from 'graphql/models/generated';
import { BaseComponent } from 'shared/base/base.component';
import { CoreDataService } from 'shared/services/core-data.service';
import { FormService } from 'shared/services/form.service';
import { PermissionsService } from 'shared/services/permissions.service';
import { StoreService } from 'shared/services/store.service';

@Component({
  selector: 'app-customer-basic-info',
  templateUrl: './basic-info.component.html',
  styleUrls: ['./basic-info.component.scss']
})
export class CustomerBasicInfoComponent extends BaseComponent implements OnInit {
  @Input() canAutoSave = true;

  readonly MAX_ADDITIONAL_NAMES = 3;

  displayMoreDetails = false;

  isCollapsed = true;

  form!: UntypedFormGroup;

  formReady = false;

  protected focusNewAdditionalName = false;

  dropdownData = {
    countries: COUNTRY_LIST
  };

  canUpdateCustomer!: boolean;

  isManufacturer!: boolean;

  get addresses(): UntypedFormArray {
    return this.form.controls.addresses as UntypedFormArray;
  }

  get additionalNames(): UntypedFormArray {
    return this.form.controls.additionalNames as UntypedFormArray;
  }

  constructor(
    protected fb: UntypedFormBuilder,
    private formService: FormService,
    protected coreDataService: CoreDataService,
    private storeService: StoreService,
    private permissionsService: PermissionsService,
    private elRef: ElementRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.init();
  }

  private init(): void {
    this.canUpdateCustomer = this.permissionsService.canUpdateCustomer();

    this.storeService.isCurrentAccountDealer()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isDealer: boolean) => {
        this.isManufacturer = !isDealer;
      });

    this.setupForm();
  }

  setupForm(): void {
    this.focusNewAdditionalName = false;
    this.storeService.setFormReady(this.formReady);

    this.storeService.getCurrentCustomer()
      .pipe(take(1))
      .subscribe((data: Customer) => {
        const customer = data || {};

        const additionalNamesToAdd = (customer.additionalNames || []);
        const additionalNames = this.fb.array(
          additionalNamesToAdd.map((name) => this.createAdditionalName(name))
        );

        const addressesToAdd = (customer.addresses && customer.addresses.length ? customer.addresses : [{}]);
        const addresses = this.fb.array(
          addressesToAdd.map((addr) => this.formService.createAddressGroup(addr))
        );

        this.form = this.fb.group({
          association: [customer.association || '', [Validators.maxLength(VALIDATORS.MAX_LENGTH.INPUT)]],
          shareholderId: [customer.shareholderId || '', [Validators.maxLength(VALIDATORS.MAX_LENGTH.INPUT)]],
          name: [customer.name || '', [Validators.required, Validators.pattern(VALIDATORS.PATTERN.NO_BLANK_STRING), Validators.maxLength(VALIDATORS.MAX_LENGTH.INPUT)]],
          additionalNames,
          addresses,
          email: [customer.email || '', [Validators.pattern(VALIDATORS.PATTERN.MAIL)]],
          phone: [customer.phone || '', [Validators.pattern(VALIDATORS.PATTERN.PHONE)]],
          fax: [customer.fax || '', [Validators.pattern(VALIDATORS.PATTERN.FAX)]],
          website: [customer.website || '', [Validators.maxLength(VALIDATORS.MAX_LENGTH.INPUT), Validators.pattern(VALIDATORS.PATTERN.WEBSITE)]],
          deliveryTerms: [customer.deliveryTerms || '', [Validators.maxLength(VALIDATORS.MAX_LENGTH.TEXTAREA)]],
          externalSales: [{ value: customer.externalSales || '', disabled: this.isManufacturer }],
          internalSales: [{ value: customer.internalSales || '', disabled: this.isManufacturer }],
          information: [customer.information || '', [Validators.maxLength(VALIDATORS.MAX_LENGTH.TEXTAREA)]]
        }, { updateOn: 'blur' });

        if (!this.isManufacturer) {
          this.form.addControl(
            'customerNumber',
            this.fb.control(customer.customerNumber || '', [
              Validators.required, Validators.pattern(VALIDATORS.PATTERN.NO_BLANK_STRING), Validators.maxLength(VALIDATORS.MAX_LENGTH.INPUT)])
          );
          this.form.addControl(
            'taxId',
            this.fb.control(customer.taxId || '', [Validators.maxLength(VALIDATORS.MAX_LENGTH.INPUT)])
          );
          this.form.addControl(
            'paymentConditions',
            this.fb.control(customer.paymentConditions || '', [Validators.maxLength(VALIDATORS.MAX_LENGTH.INPUT)])
          );
        }

        if (this.permissionsService.canUpdateCustomer()) {
          this.autoSaveSetup(customer.id);
        } else {
          this.form.disable({ emitEvent: false });

          if (this.isManufacturer) {
            this.form.controls.information.enable({ emitEvent: false });
            this.autoSaveSetup(customer.id);
          }
        }

        this.formReady = true;
        this.storeService.setFormReady(this.formReady);
      });
  }

  private autoSaveSetup(customerId: string | undefined): void {
    if (!this.canAutoSave || !customerId) {
      return;
    }

    this.displayMoreDetails = true;
    this.isCollapsed = false;

    this.form.valueChanges
      .pipe(
        filter(() => this.form.valid),
        concatMap((values) => this.coreDataService.updateCustomer(customerId, values)),
        takeUntil(this.destroyed$)
      )
      .subscribe(
        () => {
          /* TODO: disable control until save complete? */
          const hostEl = this.elRef.nativeElement as HTMLElement;
          const allInputs = Array.from(hostEl.querySelectorAll('input, select, textarea')) as HTMLElement[];
          const focusedIndex = allInputs.findIndex(el => el === document.activeElement);

          this.setupForm();

          if (focusedIndex > -1) {
            setTimeout(() => {
              const newInputs = Array.from(hostEl.querySelectorAll('input, select, textarea')) as HTMLElement[];
              newInputs[focusedIndex]?.focus();
            }, 0);
          }
        },
        (err) => { console.error(err); }
      );
  }

  createAdditionalName(name?: string): UntypedFormGroup {
    return this.fb.group({
      name: [name || '', [Validators.required, Validators.pattern(VALIDATORS.PATTERN.NO_BLANK_STRING), Validators.maxLength(VALIDATORS.MAX_LENGTH.INPUT)]]
    });
  }

  addAdditionalName(): void {
    this.focusNewAdditionalName = true;
    if (this.additionalNames.length >= this.MAX_ADDITIONAL_NAMES) {
      return;
    }
    const additionalName = this.createAdditionalName();
    this.additionalNames.push(additionalName);
  }

  removeAdditionalName(index: number): void {
    this.additionalNames.removeAt(index);
  }
}
